import React, { useEffect, useState } from 'react';
import ApiDatabase from 'server';
import { CheckIcon, ChevronDown, ClockIcon, CrossIcon, MissionIcon } from '../../../../../assets/icons';
import { dateExp, dateToEpoch } from '../../../../../helper/helper';
import UseContext from '../../../../../hooks/useContext';
import UseModal from '../../../../modal/useModal';
import Badge from '../../../../badge/badge';

const HistoryTempWorker = ({ idUser }) => {
  // Fonction pour formater la date au format "24 Janv. 2025"
  const formatDateForCard = (date) => {
    const d = new Date(date);
    const day = d.getDate();
    const monthNames = [
      'Janv.', 'Fév.', 'Mars', 'Avr.', 'Mai', 'Juin',
      'Juil.', 'Août', 'Sept.', 'Oct.', 'Nov.', 'Déc.'
    ];
    const month = monthNames[d.getMonth()];
    const year = d.getFullYear();
    return `${day} ${month} ${year}`;
  };
  const [missions, setMissions] = useState([]);
  const [candidatures, setCandidatures] = useState([]);
  const [unassignedMissions, setUnassignedMissions] = useState([]);
  const [activeFilter, setActiveFilter] = useState('missions'); // 'missions', 'pending', 'refused', 'unassigned'
  const [groupedMissions, setGroupedMissions] = useState({
    past: [],
    current: [],
    future: [],
  });
  const token = localStorage.getItem('xsrfToken');
  const { toggle } = UseModal();
  const { setTempWorkerToRefuse, setRefuseMissionId, setCurrentRefusedReason, setCurrentRefusedReasonComment, modalDataReturn } = UseContext();

  useEffect(() => {
    if (token && idUser) {
      const sortByStartDateDesc = (arr) =>
        [...(arr || [])].sort((a, b) => {
          const dA = a.duration?.datestart ? new Date(a.duration.datestart).getTime() : 0;
          const dB = b.duration?.datestart ? new Date(b.duration.datestart).getTime() : 0;
          return dB - dA;
        });

      ApiDatabase.getUserMissions(
        { token, idUser },
        (data) => {
          setMissions(sortByStartDateDesc(data));
        },
        (err) => {
          console.warn(err);
        }
      );

      ApiDatabase.getUserCandidatures(
        { token, idUser },
        (data) => {
          setCandidatures(sortByStartDateDesc(data));
        },
        (err) => {
          console.warn(err);
        }
      );

      ApiDatabase.getUserUnassignedMissions(
        { token, idUser },
        (data) => {
          setUnassignedMissions(sortByStartDateDesc(data));
        },
        (err) => {
          console.warn(err);
        }
      );
    }
  }, [idUser, token]);

  useEffect(() => {
    if (modalDataReturn?.action === 'update_refuse_candidat' && modalDataReturn.idMission) {
      setCandidatures(prev => prev.map(c => 
        c._id === modalDataReturn.idMission
          ? {
              ...c,
              refusedReason: modalDataReturn.refusedReason,
              refusedReasonId: modalDataReturn.refusedReasonId,
              refusedReasonComment: modalDataReturn.refusedReasonComment,
            }
          : c
      ));
    }
  }, [modalDataReturn]);

  useEffect(() => {
    const pastMissions = [];
    const currentMissions = [];
    const futureMissions = [];
    const now = dateToEpoch(new Date());

    for (let mission of missions) {
      const dateStart = dateToEpoch(new Date(mission.duration.datestart));
      const dateEnd = dateToEpoch(new Date(mission.duration.dateend));
      if (dateEnd.getTime() < now.getTime()) {
        pastMissions.push(mission);
      } else if (
        dateStart.getTime() <= now.getTime() &&
        dateEnd.getTime() >= now.getTime()
      ) {
        currentMissions.push(mission);
      } else {
        futureMissions.push(mission);
      }
    }
    setGroupedMissions({
      past: pastMissions,
      current: currentMissions,
      future: futureMissions,
    });
  }, [missions]);

  const pendingCandidatures = candidatures.filter(c => !c.refused && !c.isFilled);
  const refusedCandidatures = candidatures.filter(c => c.refused);

  const filters = [
    { 
      id: 'missions', 
      label: 'Attribuée', 
      count: missions.length,
      icon: <CheckIcon w={14} h={14} color={activeFilter === 'missions' ? '#FF9600' : '#6B7280'} />,
      bgColor: 'bg-green-100',
      textColor: 'text-green-600'
    },
    { 
      id: 'refused', 
      label: 'Refusées', 
      count: refusedCandidatures.length,
      icon: <CrossIcon wh={14} color={activeFilter === 'refused' ? '#FF9600' : '#6B7280'} />,
      bgColor: 'bg-red-100',
      textColor: 'text-red-600'
    },
    { 
      id: 'unassigned', 
      label: 'Dés-attribution', 
      count: unassignedMissions.length,
      icon: <MissionIcon wh={14} color={activeFilter === 'unassigned' ? '#FF9600' : '#6B7280'} />,
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-600'
    },
    { 
      id: 'pending', 
      label: 'En attente', 
      count: pendingCandidatures.length,
      icon: <ClockIcon wh={14} color={activeFilter === 'pending' ? '#FF9600' : '#6B7280'} />,
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-600'
    },
  ];

  const renderMissionItem = (mission, index, type) => {
    let iconBgColor, iconColor;
    
    if (type === 'current') {
      iconBgColor = 'bg-green-100';
      iconColor = '#059669';
    } else if (type === 'future') {
      iconBgColor = 'bg-blue-100';
      iconColor = '#3B82F6';
    } else if (type === 'unassigned') {
      iconBgColor = 'bg-purple-100';
      iconColor = '#8B5CF6';
    } else {
      iconBgColor = 'bg-gray-100';
      iconColor = '#6B7280';
    }

    return (
      <div key={index} className={'bg-white border border-gray-200 rounded-lg p-4'}>
        <div className={'flex justify-between gap-3'}>
          <div className={'flex gap-3 items-start flex-1 min-w-0'}>
            <div className={`${iconBgColor} w-10 h-10 flex justify-center items-center rounded-lg flex-shrink-0`}>
              <MissionIcon wh={20} color={iconColor} />
            </div>
            <div className={'flex flex-col gap-1 flex-1 min-w-0'}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900">{mission.title}</span>
                <span className="text-gray-400">n°{mission.id_mission}</span>
              </div>
              <div className={'text-gray-500 text-sm'}>
                {mission.name_commercial} · {mission.address?.city}
              </div>
            </div>
          </div>
          <div className={'flex flex-col items-end gap-1 flex-shrink-0'}>
            <div className={'text-gray-400 text-xs text-right whitespace-nowrap'}>
              {formatDateForCard(mission.duration.datestart)} {'>'} {formatDateForCard(mission.duration.dateend)}
            </div>
            {type === 'current' && (
              <span className="text-xs bg-green-100 text-green-600 px-2 py-0.5 rounded-full font-medium">En cours</span>
            )}
            {type === 'future' && (
              <span className="text-xs bg-blue-100 text-blue-600 px-2 py-0.5 rounded-full font-medium">À venir</span>
            )}
            {type === 'unassigned' && (
              <span className="text-xs bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full font-medium">Désassignée</span>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCandidatureItem = (candidature, index, isRefused) => {
    const iconBgColor = isRefused ? 'bg-red-100' : 'bg-yellow-100';
    const iconColor = isRefused ? '#DC2626' : '#D97706';
    
    return (
      <div key={index} className={'bg-white border border-gray-200 rounded-lg p-4'}>
        <div className={'flex justify-between gap-3'}>
          <div className={'flex gap-3 items-start flex-1 min-w-0'}>
            <div className={`${iconBgColor} w-10 h-10 flex justify-center items-center rounded-lg flex-shrink-0`}>
              {isRefused ? (
                <CrossIcon wh={20} color={iconColor} />
              ) : (
                <ClockIcon wh={20} color={iconColor} />
              )}
            </div>
            <div className={'flex flex-col gap-1 flex-1 min-w-0'}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900">{candidature.title}</span>
                <span className="text-gray-400">n°{candidature.id_mission}</span>
              </div>
              <div className={'text-gray-500 text-sm'}>
                {candidature.name_commercial} · {candidature.address?.city}
              </div>
            </div>
          </div>
          {candidature.duration && (
            <div className={'text-gray-400 text-xs text-right flex-shrink-0 whitespace-nowrap'}>
              {formatDateForCard(candidature.duration.datestart)} {'>'} {formatDateForCard(candidature.duration.dateend)}
            </div>
          )}
        </div>
        {isRefused && candidature.refusedReason && (
          <div className={'mt-2 border border-gray-200 rounded-md p-3 w-full'}>
            <div
              onClick={(e) => {
                e.stopPropagation();
                setTempWorkerToRefuse(idUser);
                setRefuseMissionId(candidature._id);
                setCurrentRefusedReason(candidature.refusedReasonId || candidature.refusedReason);
                setCurrentRefusedReasonComment(candidature.refusedReasonComment || '');
                toggle('mission_refuse_candidat');
              }}
              className="cursor-pointer inline-block"
            >
              <Badge type={'ALERT'}>
                <div className="flex items-center gap-1.5">
                  <CrossIcon wh={14} color={'#DC2626'} />
                  <span>{candidature.refusedReason}</span>
                </div>
              </Badge>
            </div>
            {candidature.refusedReasonComment && (
              <div className="text-xs text-gray-700 mt-2">
                {candidature.refusedReasonComment}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={'flex flex-col gap-4 border border-gray-100 rounded-lg p-4'}>
      {/* Filtres */}
      <div className={'flex gap-2 w-full text-sm'}>
        {filters.map((filter) => (
          <div
            key={filter.id}
            onClick={() => setActiveFilter(filter.id)}
            className={`cursor-pointer pt-1 pb-2 px-2 border-b flex items-center gap-1.5 ${
              activeFilter === filter.id
                ? 'border-yellow-500 text-yellow-500'
                : 'hover:border-yellow-500 hover:text-yellow-500 text-gray-500'
            }`}
          >
            <span>{filter.label}</span>
            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${
              activeFilter === filter.id ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-200 text-gray-600'
            }`}>
              {filter.count}
            </span>
          </div>
        ))}
      </div>

      {/* Contenu selon le filtre */}
      <div className={'flex flex-col gap-3 text-sm'}>
        {activeFilter === 'missions' && (
          <>
            {missions.length > 0 ? (
              <>
                {groupedMissions.current.map((mission, index) => {
                  return renderMissionItem(mission, `current-${index}`, 'current');
                })}
                {groupedMissions.future.map((mission, index) => {
                  return renderMissionItem(mission, `future-${index}`, 'future');
                })}
                {groupedMissions.past.map((mission, index) => {
                  return renderMissionItem(mission, `past-${index}`, 'past');
                })}
              </>
            ) : (
              <div className={'text-gray-500 text-center py-4'}>
                Aucune mission acceptée
              </div>
            )}
          </>
        )}

        {activeFilter === 'pending' && (
          <>
            {pendingCandidatures.length > 0 ? (
              pendingCandidatures.map((candidature, index) => {
                return renderCandidatureItem(candidature, index, false);
              })
            ) : (
              <div className={'text-gray-500 text-center py-4'}>
                Aucune candidature en attente
              </div>
            )}
          </>
        )}

        {activeFilter === 'unassigned' && (
          <>
            {unassignedMissions.length > 0 ? (
              unassignedMissions.map((mission, index) => {
                return renderMissionItem(mission, `unassigned-${index}`, 'unassigned');
              })
            ) : (
              <div className={'text-gray-500 text-center py-4'}>
                Aucune mission désassignée
              </div>
            )}
          </>
        )}

        {activeFilter === 'refused' && (
          <>
            {refusedCandidatures.length > 0 ? (
              refusedCandidatures.map((candidature, index) => {
                return renderCandidatureItem(candidature, index, true);
              })
            ) : (
              <div className={'text-gray-500 text-center py-4'}>
                Aucune candidature refusée
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default HistoryTempWorker;
